'use client';

import { useEffect, useRef } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { useWorkspace, useWorkspaceActions, useWorkspaceItems } from './store';
import { useFullscreenNav } from './fullscreen-nav';
import { useWorkspaceCardActions } from './card-actions-context';
import { useAuth } from '@/components/auth-provider';
import { URL_ACTIONS, getStripKeys, type UrlActionContext } from '@/lib/workspaces/url-actions';
import { markDeepLinkSpawnIntent } from '@/lib/workspaces/deep-link-intent';
import { captureFragmentTarget } from '@/lib/workspaces/fragment-target';

/** Params whose action spawns onto / takes over the canvas — surfaces like
 *  the empty-workspace Discover auto-open must yield to these arrivals.
 *  `discover` is here for the same reason with a twist: its action opens
 *  the overlay ITSELF, carrying a level the plain auto-open would race. */
const SPAWN_PARAMS = new Set(['chat', 'spawn', 'preview', 'dialog', 'discover', 'marketplace']);

/**
 * Single chokepoint for URL-triggered workspace actions. Iterates the
 * `URL_ACTIONS` registry (`lib/workspaces/url-actions.ts`) — adding a
 * new deep-link param is one entry in that file, no edits here.
 *
 * Behaviour invariants:
 *
 *  - One-shot per mount via `consumedRef` — refreshes after the URL
 *    is stripped don't re-fire intents.
 *  - All matching actions dispatched in registry order so combo
 *    URLs (`?spawn=agent&focus=@foo`) execute predictably.
 *  - `history.replaceState` is used instead of `router.push` so no
 *    RSC fetch / layout re-render happens — actions are pure
 *    client-side state changes.
 *  - `queueMicrotask` defers handlers so React Flow has its initial
 *    measurement done before any spawn lands on the canvas.
 *  - Unknown / invalid values are silently skipped by the action's
 *    `validate` hook, and unknown params stay on the URL untouched.
 */
export function WorkspaceDeepLinkConsumer() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const workspaceActions = useWorkspaceActions();
  const items = useWorkspaceItems();
  const cardActions = useWorkspaceCardActions();
  const fullscreenNav = useFullscreenNav();
  const { user } = useAuth();
  const workspace = useWorkspace();
  const consumedRef = useRef(false);

  // We need a stable snapshot of `items` for the action context but
  // also access to whatever `items` looks like at run time (for
  // late-arriving handlers, e.g. `send` which spawns then patches).
  // Use a ref to track latest.
  const itemsRef = useRef(items);
  itemsRef.current = items;

  useEffect(() => {
    if (consumedRef.current) return;
    if (!searchParams) return;

    // TERMINAL BACKSTOP against dialog recursion. `FullscreenNavProvider`
    // renders an unregistered href in a same-origin iframe; if that href is a
    // page that redirects back into `/workspace?dialog=…`, this consumer runs
    // again inside the iframe, opens the same dialog, iframes the same page —
    // and the app renders inside itself (ten levels on `/referrals` before the
    // screen gave out, 2026-08-22).
    //
    // `redirectToWorkspaceDialog` refuses to redirect once it sees
    // `embed=workspace`, and `DIALOG_ALIASES` gives every shell name a real
    // destination — but `/leaderboard` and `/marketplace` build their redirect
    // URLs by hand and so bypass the first guard. A workspace shell rendering
    // inside a frame is never something we ask for, whatever built the URL, so
    // stop here rather than adding a fourth place to remember.
    try {
      if (typeof window !== 'undefined' && window.top !== window.self) return;
    } catch {
      // Cross-origin framing throws on the `window.top` read. Not our own
      // iframe, so it is not this recursion — carry on.
    }

    // Quick reject: bail out if none of the registered params are on
    // the URL. Cheaper than building the context up front for the
    // 99% of mounts that have no deep-link payload.
    let hasAny = false;
    for (const action of URL_ACTIONS) {
      if (searchParams.has(action.param)) {
        hasAny = true;
        break;
      }
    }
    if (!hasAny) return;
    consumedRef.current = true;

    // Snapshot the params before we strip them — handlers read
    // companion params (`username`, `postId`, etc.) out of this
    // snapshot, not the live `useSearchParams`.
    const snapshot = new URLSearchParams();
    for (const [k, v] of searchParams.entries()) snapshot.set(k, v);

    const baseCtx: Omit<UrlActionContext, 'items'> = {
      params: snapshot,
      router,
      fullscreenNav,
      workspaceActions,
      cardActions,
      user: user
        ? {
            id: user.id,
            username: user.username,
            displayName: user.displayName,
            accountType: user.accountType,
          }
        : null,
      workspaceId: workspace?.id ?? null,
    };

    // Pass through the action registry; collect consumed keys for
    // stripping. We deliberately DON'T strip keys whose action's
    // `validate` rejected — the value stays on the URL so the user
    // can see what was attempted (debuggable) without firing it.
    const consumed = new Set<string>();
    const pending: Array<() => void | Promise<void>> = [];
    for (const action of URL_ACTIONS) {
      const value = snapshot.get(action.param);
      if (value == null) continue;
      const ctx: UrlActionContext = { ...baseCtx, items: itemsRef.current };
      if (action.validate && !action.validate(value, ctx)) continue;
      // Flag spawn-bearing arrivals BEFORE the strip below so later-mounting
      // surfaces (Discover's empty-workspace auto-open) can yield to them.
      if (SPAWN_PARAMS.has(action.param)) markDeepLinkSpawnIntent();
      consumed.add(action.param);
      if (action.consumes) {
        for (const k of action.consumes) consumed.add(k);
      }
      pending.push(() => action.run(value, { ...baseCtx, items: itemsRef.current }));
    }

    if (consumed.size && typeof window !== 'undefined') {
      // THE APP THE LINK NAMED IS REMEMBERED BEFORE THE QUERY GOES (S-378,
      // 2026-10-01). `?spawn=content&contentId=<row>` is the entry funnel's
      // shape (`/?app=<id>#...`), and the bridges decide from it which window
      // the page's fragment belongs to; they read the hash much later, at
      // their own handshake, by which time this strip has run. The hash is
      // kept on the URL here on purpose: the targeted bridge clears it once
      // it has handed it over (lib/workspaces/fragment-target.ts).
      captureFragmentTarget(window.location.search);
      const url = new URL(window.location.href);
      for (const k of consumed) url.searchParams.delete(k);
      const next = url.pathname + (url.search || '') + url.hash;
      window.history.replaceState(null, '', next);
    }

    // Defer the actual dispatch one microtask so React Flow + the
    // workspace store have their initial measurement done before any
    // spawn lands on the canvas (otherwise `findEmptySpace` runs
    // against an empty viewport anchor and stacks everything at
    // (0,0)).
    queueMicrotask(() => {
      for (const fn of pending) {
        try {
          void fn();
        } catch {
          /* swallow — deep links are best-effort */
        }
      }
    });
  }, [
    searchParams,
    router,
    fullscreenNav,
    workspaceActions,
    cardActions,
    user,
    workspace?.id,
  ]);

  return null;
}

// Re-export the strip list so callers / tests can inspect it.
export { getStripKeys };
